// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import type { Practitioner, Reference } from '@medplum/fhirtypes';
import { IconCircleOff } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { ProviderSelect } from '../../components/tasks/ProviderSelect';

interface CreateTriageTaskModalProps {
  opened: boolean;
  title: string;
  description: string;
  patientName: string | undefined;
  defaultAssignee: Practitioner | undefined;
  submitting: boolean;
  onClose: () => void;
  onCreate: (assignee: Reference<Practitioner>, details: TaskDetails) => void;
}

export interface TaskDetails {
  dueDate: string;
  priority: 'routine' | 'urgent' | 'asap';
  description: string;
}

export function CreateTriageTaskModal({
  opened,
  title,
  description,
  patientName,
  defaultAssignee,
  submitting,
  onClose,
  onCreate,
}: CreateTriageTaskModalProps): JSX.Element {
  const [assignee, setAssignee] = useState<Reference<Practitioner> | undefined>();
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskDetails['priority']>('routine');
  const [descriptionText, setDescriptionText] = useState('');

  useEffect(() => {
    if (opened && defaultAssignee) {
      setAssignee(createReference(defaultAssignee));
    }
    if (opened) {
      setDueDate(new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10));
      setPriority('routine');
      setDescriptionText(description);
    }
  }, [defaultAssignee, description, opened]);

  const handleCreate = (): void => {
    if (!assignee) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        message: 'Select an assignee before creating the task.',
      });
      return;
    }
    if (!descriptionText.trim()) {
      notifications.show({ color: 'red', icon: <IconCircleOff size={16} />, message: 'Describe the task first.' });
      return;
    }
    onCreate(assignee, { dueDate, priority, description: descriptionText.trim() });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Create task" centered size="md">
      <Stack gap="md">
        <Stack gap={4}>
          <Text fw={700}>{title}</Text>
          {patientName && (
            <Text size="sm" c="dimmed">
              {patientName}
            </Text>
          )}
          <Text size="sm">{description}</Text>
        </Stack>

        <ProviderSelect
          name="triage-task-assignee"
          label="Assign to"
          placeholder="Select assignee"
          required
          value={assignee}
          onChange={setAssignee}
        />

        <Group grow align="flex-start">
          <TextInput
            label="Due date"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.currentTarget.value)}
          />
          <Select
            label="Priority"
            data={[
              { value: 'routine', label: 'Routine' },
              { value: 'asap', label: 'Today' },
              { value: 'urgent', label: 'Urgent' },
            ]}
            value={priority}
            onChange={(value) => setPriority((value as TaskDetails['priority'] | null) ?? 'routine')}
          />
        </Group>
        <Textarea
          label="Task"
          required
          placeholder="What needs doing?"
          minRows={3}
          value={descriptionText}
          onChange={(event) => setDescriptionText(event.currentTarget.value)}
        />

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleCreate} loading={submitting}>
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
